import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Avatar,
  Tag,
  Button,
  Spin,
  Empty,
  message,
  Badge,
} from "antd";
import {
  UserOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  LeftOutlined
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { getExpertServiceDetails } from "PlatForms/SelfLMS/Services/ExpertService/ExpertServiceLearner";
import SoftBox from "Common/hooks/StyleFeatures/SoftBox";
import SoftTypography from "Common/hooks/StyleFeatures/SoftTypography";

const { Title, Text, Paragraph } = Typography;

const ExpertServiceDetailPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load service details
  useEffect(() => {
    if (serviceId) {
      loadServiceDetails();
    }
  }, [serviceId]);

  const loadServiceDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getExpertServiceDetails(serviceId);
      console.log("...................servoce detai; ",data);
      setService(data);
    } catch (err) {
      setError(err.message || "Failed to load service details");
      message.error("Failed to load service details");
    } finally {
      setLoading(false);
    }
  };

 

  // Format duration
  const formatDuration = (minutes) => {
    if (!minutes) return "0 min";
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  // Format price
  const formatPrice = (price, currency = "USD") => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <SoftBox py={3}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <Title level={4} style={{ marginTop: 16 }}>Loading service details...</Title>
        </div>
      </SoftBox>
    );
  }

  if (error || !service) {
    return (
      <SoftBox py={3}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={3}>Service Not Found</Title>
          <Text>{error || "The requested service could not be found."}</Text>
          <Button 
            type="primary" 
            onClick={() => navigate('/purchased-expert-services')} 
            style={{ marginTop: 16 }}
          >
            Back to Services
          </Button>
        </div>
      </SoftBox>
    );
  }

  return (
    <SoftBox py={3}>
      {/* Header with Back Button */}
      <SoftBox mb={3} display="flex" alignItems="center">
        <Button 
          type="text" 
          icon={<LeftOutlined />} 
          onClick={() => navigate('/purchased-expert-services')}
          style={{ marginRight: 16 }}
        >
          Back to Services
        </Button>
        <SoftBox>
          <SoftTypography variant="h4" fontWeight="medium">
            {service.title}
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            Expert Service Details
          </SoftTypography>
        </SoftBox>
      </SoftBox>

      <Row gutter={[24, 24]}>
        {/* Left Column - Service and Expert Info */}
        <Col xs={24} lg={16}>
          {/* Service Cover Image and Title */}
          <Card 
            style={{ marginBottom: 24 }}
            cover={
              <div style={{ 
                height: 100, 
                background: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.5)), url(${service.expert?.profileImage || '/default-expert.jpg'})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                alignItems: 'flex-end',
                padding: 12
              }}>
                <div style={{ color: 'white' }}>
                  <Title level={3} style={{ color: 'white', margin: 0 }}>
                    {service.title}
                  </Title>
                </div>
              </div>
            }
          >
            <div style={{ padding: '0 24px 24px' }}>
              {/* Service Status */}
              <div style={{ marginBottom: 16 }}>
                <Badge 
                  status={service.isActive ? 'success' : 'default'} 
                  text={service.isActive ? 'Active' : 'Inactive'} 
                />
              </div>

              {/* Service Description */}
              <div style={{ marginBottom: 24 }}>
                <Title level={4}>About This Service</Title>
                <Paragraph style={{ fontSize: '16px' }}>
                  {service.description}
                </Paragraph>
              </div>

              {/* Expert Information */}
              <Card 
                title="Expert Information" 
                size="small" 
                style={{ marginBottom: 24 }}
              >
                <Row gutter={16} align="middle">
                  <Col>
                    <Avatar 
                      src={service.expert?.profileImage} 
                      icon={<UserOutlined />}
                      size={64} 
                    />
                  </Col>
                  <Col flex="auto">
                    <Title level={4} style={{ margin: 0 }}>
                      {service.expert?.firstName} {service.expert?.lastName}
                    </Title>
                    <div style={{ marginBottom: 8 }}>
                      <Text type="secondary">{service.expert?.expertCode}</Text>
                    </div>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Text type="secondary">
                          <CalendarOutlined style={{ marginRight: 4 }} />
                          {service.expert?.yearsOfExperience || 0} years experience
                        </Text>
                      </Col>
                     
                    </Row>
              
                  </Col>
                </Row>
              </Card>

              {/* Expertise Areas */}
              <Card 
                title="Expertise Areas" 
                size="small" 
                style={{ marginBottom: 24 }}
              >
                <div>
                  {service.expert?.selectedCategoryTags?.map((tag, index) => (
                    <Tag key={tag.id || index} style={{ marginBottom: 8 }}>
                      {tag.name}
                    </Tag>
                  ))}
                </div>
              </Card>
            </div>
          </Card>
        </Col>

        {/* Right Column - Pricing Options */}
        <Col xs={24} lg={8}>
       <Card title="Pricing Options" style={{ position: 'sticky', top: 24 }}>
  {service.pricing ? (
    <div style={{ padding: '16px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Text strong style={{ fontSize: '18px', color: '#9A6B0E' }}>
            {formatPrice(service.pricing.unitPrice, service.pricing.currency)}
          </Text>
          <div style={{ marginTop: 4 }}>
            <ClockCircleOutlined style={{ marginRight: 4 }} />
            {formatDuration(service.pricing.unitDurationMinutes)}
          </div>
          {service.pricing.notes && (
            <div style={{ marginTop: 4 }}>
              <Text type="secondary">{service.pricing.notes}</Text>
            </div>
          )}
          <div style={{ marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Valid from: {formatDate(service.pricing.validFrom)}
            </Text>
          </div>
        </div>
     
      </div>
    </div>
  ) : (
    <Empty description="No pricing options available" />
  )}
</Card>
        </Col>
      </Row>

   
    </SoftBox>
  );
};

export default ExpertServiceDetailPage;